import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import * as api from '../../utils/api';
import { useInterval } from '../../utils/utils';
import { APPLE_ITUNES_APP_META } from '../../utils/appLinks';
// Modals
import {
  UpdatePhotos,
  UpdateDetails,
  UpdateDisponibility,
  UpdateAdresse,
  CancelModal,
  AcceptCounterProposalModal,
} from '../../components/customerArea/courseDetails/Modals';
import NoFixerModal from '../../modals/NoFixerModal';
// Components
import CourseSkeleton from '../CourseSkeleton';
import Link from '../../components/shared/atoms/Link';
import Heading from '../../components/shared/atoms/Heading';
import Container from '../../components/shared/atoms/Container';
import CourseBanner from '../../components/customerArea/courseDetails/CourseBanner';
import SideActions from '../../components/customerArea/courseDetails/SideActions';
import CourseDetails from '../../components/customerArea/courseDetails/CourseDetails';
import CourseMobileCtaBar from '../../components/customerArea/courseDetails/CourseMobileCtaBar';
import RequestCard from '../../components/customerArea/courseDetails/RequestCard';
import AngleLeft16 from '../../components/icons/AngleLeft16';

// §8 — 2-column hub: content + a 340px right action column that sticks on
// desktop; collapses to a single column below 992px (spec breakpoint).
const HubGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-bottom: 56px;

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 56px;
    align-items: start;
  }
`;

const HubMain = styled.div`
  min-width: 0;
`;

const HubAside = styled.div`
  min-width: 0;

  @media (min-width: 992px) {
    position: sticky;
    top: 24px;
  }
`;

const Course = ({ match }) => {
  const { t } = useTranslation();
  const [relatedPhotos, setRelatedPhotos] = useState([]);
  const { client } = useSelector((s) => s);
  const history = useHistory();
  const [course, setCourse] = useState(false);
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [additionalProps, setAdditionalProps] = useState({});
  const [waitinglistSent, setWaitingListSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [counterProposals, setCounterProposals] = useState([]);
  const [hasCounterProposals, setHasCounterProposals] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showCounterProposalModal, setShowCounterProposalModal] = useState(
    false,
  );

  const _submitWaitingList = async (values, { setError, setSubmitting }) => {
    const waiting = await api.post(`/waitinglist`, {
      position: course.position,
      product_id: course.product_id,
      ...values,
    });
    if (waiting.ok) setWaitingListSent(true);
    else {
      setError(t('wronginformations'));
      setSubmitting(false);
    }
  };

  const getRelatedPhotos = async () => {
    const getRelatedPhotos = await api.get(
      `/courses/${match.params.cid}/photos`,
    );
    // api.get resolves to undefined/null on error or empty body — keep
    // relatedPhotos an array so consumers can rely on the invariant.
    setRelatedPhotos(Array.isArray(getRelatedPhotos) ? getRelatedPhotos : []);
  };

  const getCourse = async () => {
    const result = await api.get(`/courses/${match.params.cid}`);

    if (!result || result.error_message) {
      history.push(`/login${window.location.search}?redir=${match.params.cid}`);
      return;
    }
    setCourse(result);

    setLoading(false);
  };

  const getRequest = async () => {
    let requests = await api.get(`/requests?course_ids[]=${match.params.cid}`);
    if (!requests || requests.error_message) {
      return;
    }
    requests = requests.map((r) => {
      r.data = JSON.parse(r.data);
      return r;
    });
    setRequests(requests.filter((r) => r.seen == 0));
  };

  const getCounterProposals = async () => {
    if (course?.state === 'dispatched') {
      const response = await api.getCounterProposals(match.params.cid);
      if (response?.success && response.data) {
        const available = response.data.filter((p) => p.is_available);
        setCounterProposals(response.data);
        setHasCounterProposals(available.length > 0);
      }
    }
  };

  const handleSelectProposal = (proposal) => {
    setSelectedProposal(proposal);
    setShowCounterProposalModal(true);
  };

  const handleCounterProposalAccepted = (data) => {
    // Refresh course data after acceptance
    getCourse();
    setShowCounterProposalModal(false);
    setSelectedProposal(null);
  };

  const cancelCourse = async (cancelCategories) => {
    const cancelCategorie = Object.values(cancelCategories).find(
      (c) => c.isChecked === true,
    );
    await api.put(`/courses/${course.id}`, {
      status: 'ANNULE',
    });
    setCourse({ ...course, state: 'cancelled', status: 'ANNULE' });
    if (cancelCategorie)
      await api.put(`/courses/${course.id}/ctags`, { id: cancelCategorie.id });
  };

  // Modal States and Functions
  const propsModal = {
    course,
    setCourse,
    showModal,
    getRelatedPhotos,
    getCourse,
    getRequest,
    cancelCourse,
    additionalProps,
  };
  const openModal = (which, additionalProps = {}) => {
    setShowModal(which);
    setAdditionalProps(additionalProps);
  };

  useEffect(() => {
    getCourse().catch(() => {});
    getRelatedPhotos().catch(() => {});
    getRequest().catch(() => {});
  }, []);

  // Fetch counter proposals when course is loaded and in dispatched state
  useEffect(() => {
    if (course && course.state === 'dispatched') {
      getCounterProposals().catch(() => {});
    }
  }, [course?.state]);

  useInterval(() => {
    getCourse().catch(() => {});
    getRelatedPhotos().catch(() => {});
    getRequest().catch(() => {});
  }, 300000);

  if (loading) {
    return <CourseSkeleton />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    await _submitWaitingList();
    window.location.href = '/';
  };

  return (
    <>
      {/*  All Modal */}
      <UpdatePhotos {...{ ...propsModal, openModal }} />
      <UpdateDetails {...{ ...propsModal, openModal }} />
      <UpdateAdresse {...{ ...propsModal, openModal }} />
      <UpdateDisponibility {...{ ...propsModal, openModal }} />
      <CancelModal {...{ ...propsModal, openModal }} />
      <NoFixerModal
        open={showModal === 'NO_FIXER'}
        waitinglistSent={waitinglistSent}
        onSubmit={handleSubmit}
        close={() => (window.location.href = '/')}
        fixer={null}
      />
      <AcceptCounterProposalModal
        open={showCounterProposalModal}
        proposal={selectedProposal}
        courseId={course?.id}
        onClose={() => {
          setShowCounterProposalModal(false);
          setSelectedProposal(null);
        }}
        onAccepted={handleCounterProposalAccepted}
        onError={(type) => {
          if (type === 'unavailable') {
            getCounterProposals();
          }
        }}
      />

      {/*  -------- */}
      {/* Smart App Banner iOS, sur les mêmes pages que l'encart AppPromoCard :
          depuis bf5a8cd2 celui-ci s'affiche sur toute course active et sur le hub
          post-intervention, donc la bannière les suit. */}
      <Helmet meta={[APPLE_ITUNES_APP_META]} />
      <Container fluid>
        <Container mt={{ xs: 3, lg: 5 }}>
          <Link to="/account" before={<AngleLeft16 />} color="blue.electric">
            {t('cta.back')}
          </Link>
        </Container>
      </Container>
      <Container fluid mt={4}>
        <CourseBanner course={course} openModal={openModal} />
        <Container mt={{ xs: 7, lg: 10 }}>
          <HubGrid>
            <HubMain>
              <CourseDetails
                course={course}
                client={client}
                openModal={openModal}
                relatedPhotos={relatedPhotos}
                counterProposals={counterProposals}
                hasCounterProposals={hasCounterProposals}
                onCounterProposalAccepted={handleCounterProposalAccepted}
                onCounterProposalsRefresh={getCounterProposals}
                onSelectProposal={handleSelectProposal}
              />
            </HubMain>
            <HubAside>
              <SideActions course={course} openModal={openModal} />
              {requests.length > 0 && (
                <>
                  <Heading level={5} mt={9}>
                    {t('course.request.title')}
                  </Heading>
                  {requests.map((request) => {
                    return <RequestCard key={request.id} request={request} />;
                  })}
                </>
              )}
            </HubAside>
          </HubGrid>
        </Container>
      </Container>
      <CourseMobileCtaBar course={course} />
    </>
  );
};

export default Course;
